'use client';

import { c as _c } from "react/compiler-runtime";
import * as Headless from '@headlessui/react';
import type React from 'react';
import { forwardRef } from 'react';
import { cn } from '../../../shared/cn';
import { Link } from '../../actions/Link';
import { Badge, type BadgeProps } from './Badge';
import { TouchTarget } from './TouchTarget';
type BadgeButtonProps = BadgeProps & {
  className?: string;
  children: React.ReactNode;
};
type BadgeButtonButtonProps = BadgeButtonProps & Omit<Headless.ButtonProps, 'as' | 'className'>;
type BadgeButtonLinkProps = BadgeButtonProps & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'> & {
  href: string;
};
export type BadgeButtonPropsWithRef = BadgeButtonButtonProps | BadgeButtonLinkProps;
export const BadgeButton = forwardRef(function BadgeButton(t0, ref) {
  const $ = _c(24);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      color: t1,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const color = t1 === undefined ? "zinc" : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("group relative inline-flex focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const classes = t2;
  if ("href" in props && typeof props.href === "string") {
    const linkProps = props as BadgeButtonLinkProps;
    const t3 = ref as React.ForwardedRef<HTMLAnchorElement>;
    let t4;
    if ($[7] !== children || $[8] !== classes || $[9] !== color) {
      t4 = <TouchTarget><Badge color={color} className={classes}>{children}</Badge></TouchTarget>;
      $[7] = children;
      $[8] = classes;
      $[9] = color;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== linkProps || $[12] !== t3 || $[13] !== t4) {
      t5 = <Link {...linkProps} ref={t3}>{t4}</Link>;
      $[11] = linkProps;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  const buttonProps = props as BadgeButtonButtonProps;
  const t3 = buttonProps.type ?? "button";
  let t4;
  if ($[15] !== children || $[16] !== classes || $[17] !== color) {
    t4 = <TouchTarget><Badge color={color} className={classes}>{children}</Badge></TouchTarget>;
    $[15] = children;
    $[16] = classes;
    $[17] = color;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== buttonProps || $[20] !== ref || $[21] !== t3 || $[22] !== t4) {
    t5 = <Headless.Button {...buttonProps} type={t3} className="cursor-pointer data-disabled:cursor-not-allowed" ref={ref}>{t4}</Headless.Button>;
    $[19] = buttonProps;
    $[20] = ref;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
});